import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { StringUtils } from "../features/utils/StringUtils";

export interface UseFilterableListOptions<T> {
  /** The full, unfiltered set. Prefer a stable identity across renders. */
  items: T[];
  /**
   * Every string the query is matched against for one item — the name first,
   * then any aliases. Called per item per keystroke, so keep it cheap.
   */
  getSearchStrings: (item: T) => (string | undefined | null)[];
  /**
   * Invoked with the highlighted item on Enter. Return exactly `false` to
   * keep the list open — for confirms that aren't ready yet, e.g. the prop
   * library waiting on the selection's content to load.
   */
  onSelect?: (item: T) => void | boolean;
  /** Invoked on Escape, and after a successful Enter. */
  onClose?: () => void;
  /**
   * Invoked with the highlighted item on Space — the file explorer's
   * multi-select toggle. Only fires when focus is outside a text field, where
   * Space is just a space.
   */
  onSpace?: (item: T) => void;
  /**
   * Supply this to drive the query from outside — a Redux slice, a URL param,
   * anything that also has to survive the dialog closing. Leave it undefined
   * and the hook keeps the query in its own state.
   */
  filter?: string;
  /** Called by `setFilter` when the query is controlled. */
  onFilterChange?: (filter: string) => void;
  /**
   * Results per row. At 1 (the default) ↑ ↓ step one item and ← → do nothing.
   * Above 1 the list is treated as a grid: ← → step one, ↑ ↓ step a full row.
   */
  columns?: number;
  /**
   * Overrides the arrow-key geometry entirely: given the current index and a
   * direction, return the next index. For layouts a plain row-step gets wrong
   * — a grid split into per-group blocks, most obviously. When set, all four
   * arrows are handled regardless of `columns`.
   */
  moveHighlight?: (index: number, direction: "up" | "down" | "left" | "right") => number;
  /** Whether the keyboard listener is live from the first render. */
  initiallyOpen?: boolean;
  /**
   * Stands the keyboard listener down without closing the list. Set it while
   * an inline editor is open inside the list — the listener captures on
   * `window`, so Enter, Escape and the arrows would otherwise be taken from
   * the field before it ever sees them.
   */
  suspended?: boolean;
  /**
   * When set, "/" moves focus to whatever `searchInputRef` points at — unless
   * the user is already typing somewhere, where a slash is just a slash.
   */
  focusSearchOnSlash?: boolean;
}

const isTextEntryTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  if (!element || typeof element.tagName !== "string") return false;
  if (element.isContentEditable) return true;

  const tagName = element.tagName.toLowerCase();
  return tagName === "input" || tagName === "textarea" || tagName === "select";
};

/**
 * Plain arrows always belong to list navigation — even mid-query, where the
 * cost is only that a typo is fixed with backspace instead of the caret.
 * Modified arrows (Shift-select, Ctrl/Alt word-jump) stay with the field.
 */
const hasModifier = (event: KeyboardEvent): boolean =>
  event.shiftKey || event.ctrlKey || event.metaKey || event.altKey;

/**
 * Filter-and-arrow behaviour shared by the browse dialogs and the sequence
 * context menu: subsequence matching over name plus aliases, a highlight that
 * survives filtering, and the ↑ ↓ / Enter / Escape / "/" keystrokes.
 *
 * The hook owns no markup — a caller decides how the items are drawn and where
 * `searchInputRef` is attached.
 */
export default function useFilterableList<T>(
  options: UseFilterableListOptions<T>
) {
  const {
    items,
    getSearchStrings,
    onSelect,
    onClose,
    filter: controlledFilter,
    onFilterChange,
    columns = 1,
    moveHighlight,
    onSpace,
    initiallyOpen = false,
    focusSearchOnSlash = false,
    suspended = false,
  } = options;

  const [internalFilter, setInternalFilter] = useState<string>("");
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [open, setOpen] = useState<boolean>(initiallyOpen);
  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const isControlled = controlledFilter !== undefined;
  const filter = isControlled ? controlledFilter : internalFilter;

  const setFilter = useCallback(
    (next: string) => {
      if (!isControlled) setInternalFilter(next);
      onFilterChange?.(next);
    },
    [isControlled, onFilterChange]
  );

  // Held in refs so an inline lambda from the caller does not re-register the
  // window listener on every render.
  const onSelectRef = useRef(onSelect);
  const onCloseRef = useRef(onClose);
  const onSpaceRef = useRef(onSpace);
  const moveHighlightRef = useRef(moveHighlight);
  useEffect(() => {
    onSelectRef.current = onSelect;
    onCloseRef.current = onClose;
    onSpaceRef.current = onSpace;
    moveHighlightRef.current = moveHighlight;
  });

  const filteredItems = useMemo(() => {
    if (!filter) return items;

    return items.filter((item) =>
      StringUtils.SubsequenceMatchAny(filter, getSearchStrings(item))
    );
  }, [items, filter, getSearchStrings]);

  // A new query starts at the top. Done during render rather than in an effect
  // so the caller never paints one frame with the previous highlight.
  const previousFilterRef = useRef(filter);
  if (previousFilterRef.current !== filter) {
    previousFilterRef.current = filter;
    if (selectedIndex !== 0) setSelectedIndex(0);
  }

  // The item set can also shrink without the filter changing — a category
  // switch, a page of results arriving — which would leave the highlight past
  // the end of the list.
  useEffect(() => {
    if (selectedIndex > 0 && selectedIndex >= filteredItems.length) {
      setSelectedIndex(Math.max(filteredItems.length - 1, 0));
    }
  }, [filteredItems, selectedIndex]);

  const incrementIndex = useCallback(
    (amount: number) => {
      setSelectedIndex((previous) => {
        const next = previous + amount;
        if (next < 0) return 0;
        if (next >= filteredItems.length)
          return Math.max(filteredItems.length - 1, 0);
        return next;
      });
    },
    [filteredItems.length]
  );

  const focusSearch = useCallback(() => {
    const input = searchInputRef.current;
    if (!input) return;

    input.focus();
    input.select?.();
  }, []);

  useEffect(() => {
    if (!open || suspended) return;

    const rowStep = Math.max(columns, 1);

    const isGrid = rowStep > 1 || !!moveHighlightRef.current;

    const moveTo = (direction: "up" | "down" | "left" | "right") => {
      const custom = moveHighlightRef.current;
      if (custom) {
        setSelectedIndex((previous) => {
          const next = custom(previous, direction);
          return Math.min(Math.max(next, 0), Math.max(filteredItems.length - 1, 0));
        });
        return;
      }

      if (direction === "up") incrementIndex(-rowStep);
      else if (direction === "down") incrementIndex(rowStep);
      else incrementIndex(direction === "left" ? -1 : 1);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key.startsWith("Arrow") && hasModifier(event)) return;

      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveTo("up");
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        moveTo("down");
      } else if (event.key === "ArrowLeft" && isGrid) {
        event.preventDefault();
        moveTo("left");
      } else if (event.key === "ArrowRight" && isGrid) {
        event.preventDefault();
        moveTo("right");
      } else if (event.key === "Enter") {
        const item = filteredItems[selectedIndex];
        if (item) {
          // preventDefault is load-bearing, not politeness. Closing the
          // dialog makes MUI restore focus to the button that opened it,
          // DURING this very dispatch — and Enter's default action then
          // activates that button, reopening the dialog in the same
          // keystroke. jsdom performs no such default action, so only a real
          // browser catches this.
          event.preventDefault();
          // Closing in `finally` so a failure while acting on the selection
          // cannot leave the list stranded open. The error still propagates.
          // A deliberate `false` from onSelect skips the close instead.
          let keepOpen = false;
          try {
            keepOpen = onSelectRef.current?.(item) === false;
          } finally {
            if (!keepOpen) onCloseRef.current?.();
          }
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current?.();
      } else if (
        event.key === " " &&
        onSpaceRef.current &&
        !isTextEntryTarget(event.target)
      ) {
        const item = filteredItems[selectedIndex];
        if (item) {
          event.preventDefault();
          onSpaceRef.current(item);
        }
      } else if (
        focusSearchOnSlash &&
        event.key === "/" &&
        !isTextEntryTarget(event.target)
      ) {
        event.preventDefault();
        // stopPropagation, not just preventDefault: the editor binds "/" on
        // `document` to open the advanced search (useShortcut/ShortCuts.Search)
        // and it only bows out when the target is already a text field — which
        // it is not, since that is precisely the case we are here to handle.
        // Left alone, one "/" both focuses this dialog's search AND opens the
        // advanced search behind it. We capture on `window`, so stopping here
        // means the document listener never sees the key.
        event.stopPropagation();
        focusSearch();
      }
    };

    // Capture phase, deliberately. A MUI Modal's root calls
    // `stopPropagation()` on Escape whether or not it was given an `onClose`,
    // so a bubble-phase listener anywhere above the dialog content never sees
    // it. Capturing on the window runs before every descendant handler, and
    // works no matter where focus currently sits inside the dialog.
    window.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    open,
    suspended,
    filteredItems,
    selectedIndex,
    incrementIndex,
    focusSearch,
    focusSearchOnSlash,
    columns,
  ]);

  const reset = useCallback(() => {
    setFilter("");
    setSelectedIndex(0);
  }, [setFilter]);

  return {
    filter,
    setFilter,
    filteredItems,
    selectedItem: filteredItems[selectedIndex] as T | undefined,
    selectedIndex,
    setSelectedIndex,
    incrementIndex,
    reset,
    open,
    setOpen,
    searchInputRef,
    focusSearch,
  };
}
